import { Parser } from '@json2csv/plainjs'
import { cloneDeep } from 'lodash'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import Moment from 'moment'
import OLFormatGeoJSON from 'ol/format/GeoJSON'
import { transform as OLProjTransform } from 'ol/proj'
import OLSourceImageWMS from 'ol/source/ImageWMS'

import PoziAPICKANAPI from 'api/ckanapi'
import PoziAPIData from 'api/data'
import * as PoziApiGml from 'api/gml'
import PoziAPIGSheet from 'api/gsheet'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import AddId from 'config/AddId'
import ApplyTransformers from 'config/ApplyTransformers'
import GetAddressFromHereMaps from 'data/GetAddressFromHereMaps'
import getChildData from 'data/GetChildData'
import GetPropertiesAll from 'data/GetPropertiesAll'
import PoziJotForm from 'data/jotform'
import Downloader from 'drawer/tabs/tools/DownloadMap/Downloader'
import { GetGeometryPropertiesFromTurfFeatureCollection } from 'map/GetGeometryProperties'
import PoziNotifications from 'utils/notifications'

export const MAX_GET_URL_LENGTH_GET = 2000
export const MAX_GET_URL_LENGTH_POST = 10000

export async function DownloadChildDataset (parentGeoJSON, childLayer) {
  // console.debug('Download - DownloadChildDataset ', childLayer.titleid, parentGeoJSON, childLayer)

  const features = get(parentGeoJSON, 'features', [])
  const results = []
  for (const key in features) {
    const feature = features[key]
    const p = get(feature, 'properties', {})

    const propertiesAll = GetPropertiesAll(p) // sanity checks.

    const currentTarget = { propertiesAll }
    const child = getChildData(currentTarget, childLayer)

    // console.debug('DownloadChildDataset - child', childLayer.titleid, child, currentTarget)

    try {
      const data = await downloadDataAsync(child, currentTarget)
      const propertiesArr = extractPropertiesFromGeoJSON(data)
      results.push(...propertiesArr)
    } catch (err) {
      console.log('Download - unable to download child dataset for ', child.titleid, err)
    }
  }

  // console.debug('DownloadChildDataset results', results)

  return results
}

// This is similar to DownloadChildDataset but it builds a comma delimited list of
// the feature props, and attempts a single query.
// The intent is to be used as part of IN statements. E.g. id IN ('1','2','3'...)
// If the dataset query does not support this style of param replacement the download will likely fail to
// return any results, or will return some unexpected result.
export async function DownloadChildDatasetSingleQueryMultiParam (parentGeoJSON, childLayer, returnGeoJSON = false) {
  const childUrl = PoziUtils.getLayerUrl(childLayer)

  if (!childUrl) {
    console.log('Download - invalid URL', childUrl)
    return null
  }

  const features = get(parentGeoJSON, 'features', [])
  if (features.length === 0) {
    console.log('Download - no features to download.', parentGeoJSON)
    return null
  }

  // console.debug('DownloadChildDataset - features', features, parentGeoJSON)

  const feature = features[0] // Sample feature to get basic dataset info. Assumes all features have same properties.
  const p = get(feature, 'properties', {})

  // TODO: check if using the parent geometry properties is a good idea
  const parentGeomProperties = GetGeometryPropertiesFromTurfFeatureCollection(parentGeoJSON)

  // console.debug('DownloadChildDataset - geomProperties', parentGeomProperties)

  const propertiesAll = GetPropertiesAll(p) // sanity checks.
  const currentTarget = { propertiesAll }
  const child = getChildData(currentTarget, childLayer)

  // console.debug('DownloadChildDatasetSingleQueryMultiParam - child', childLayer.titleid, child, currentTarget)

  // Create an array of features properties objects
  const featuresProps = features.map(feature => feature.properties)

  // console.debug('DownloadChildDatasetSingleQueryMultiParam - featuresProps', childLayer.titleid, featuresProps)

  // Use convenience function for replacing URL variables with the actual values from the
  // feature properties and the parent geometry properties
  const combinedProps = [parentGeomProperties, ...featuresProps]
  const multiParamChildUrl = PoziUtils.replaceUrlVariablesWithPropsValues(childUrl, combinedProps)

  // console.debug('DownloadChildDatasetSingleQueryMultiParam - url', multiParamChildUrl, combinedProps)

  let propertiesArr = []
  try {
    // Make a copy of the child object and replace the original URL with the multi-param one
    const multiParamChild = cloneDeep(child)
    multiParamChild.url = multiParamChildUrl

    const data = await downloadDataAsync(multiParamChild, currentTarget)
    propertiesArr = extractPropertiesFromGeoJSON(data)

    if (returnGeoJSON) {
      console.debug('DownloadChildDataset - returnGeoJSON is set. Returning GeoJSON.')

      return data
    }
  } catch (err) {
    console.log('Download - unable to download child dataset for ', child.titleid, err)
  }

  if (propertiesArr.length === 0) {
    PoziNotifications().notify('No data found for downloading')

    return propertiesArr
  }

  try {
    const parser = new Parser()
    const csv = parser.parse(propertiesArr)
    const filename = childLayer.titleid + '_' + Moment().format('YYYYMMDDHHmmss') + '.csv'
    browserDownloadString(filename, csv)
  } catch (err) {
    console.error(err)
    PoziNotifications().warning('Something went wrong... Error with parsing download data:' + err.message || '')
  }

  return propertiesArr
}

function extractPropertiesFromGeoJSON (geojson) {
  const features = get(geojson, 'features', [])
  const results = []
  for (const key in features) {
    const feature = features[key]
    const p = get(feature, 'properties', {})
    const propertiesAll = GetPropertiesAll(p) // sanity checks.

    // Remove the $id field as that is not relevant in downloads
    delete propertiesAll.$id

    results.push(propertiesAll)
  }
  return results
}

function browserDownloadString (filename, data) {
  Downloader(data, filename)
}

function errorRetrievingDataFn (error, callback) {
  console.log('Download - errorRetrievingData', error)

  const errorRetrievingData = {
    errorRetrievingData: true,
    error
  }

  return callback(errorRetrievingData)
}

export async function downloadDataAsync (child, currentTarget) {
  return new Promise((resolve, reject) => {
    downloadData(child, currentTarget, (result) => {
      const errorRetrievingData = get(result, 'errorRetrievingData', false)
      if (errorRetrievingData) {
        return reject(result)
      }
      return resolve(result)
    })
  })
}

const cache = []

function setPanelDataCache (id, data) {
  try {
    const cacheTimeout = Model.get('config.cacheTimeoutSeconds', 60)
    if (cacheTimeout > 0) {
      if (isNil(data)) {
        console.log('Download - dataset is null... nothing to cache', id)
        return null
      }
      const dataCopy = JSON.parse(JSON.stringify((data)))
      cache[id] = dataCopy
      window.setTimeout(() => {
        cache[id] = null
      }, cacheTimeout * 1000) // TODO: Set this to 60 seconds
    }
  } catch (err) {
    console.log('Download - failed to cache this dataset', id, err)
  }
}

function getPanelDataCache (id) {
  try {
    const data = cache[id]
    if (isNil(data)) {
      return null
    }
    return JSON.parse(JSON.stringify((data)))
  } catch (err) {
    console.log('Download - failed to cache this dataset', id, err)
  }
}

function getDataCallback (id, layerConf, geojson, callback) {
  const disableCache = Model.get('config.disableCache', false)

  // The following will add an `$id` field to the properties as that is sometimes
  // required for selecting a feature on the map (when the id field is `$id`)
  AddId(geojson, layerConf)

  if (!disableCache) {
    setPanelDataCache(id, geojson)
  }
  callback(geojson)
}

const generateCacheId = (titleid, url, currentTarget = null) => {
  let cacheId = titleid + url + currentTarget?.propertiesAll?.$wkt || ''
  cacheId = cacheId.replace(/[^a-zA-Z0-9]+/g, '')

  return cacheId
}

export function downloadData (child, currentTarget, callback) {
  const { titleid, loadertypelower, url, fallbackUrls, urlParamsObj } = child

  console.debug('Download - downloadData', titleid, loadertypelower, url, urlParamsObj)

  const layerConf = PoziUtils.getLayerFromTitleID(titleid)

  let newUrl = url

  // Add the spatial intersect filter param to the request (if needed)
  const filterParam = getFilterParam(child, currentTarget)
  if (filterParam) {
    try {
      const newUrlObj = new URL(url)
      newUrlObj.searchParams.set('FILTER', filterParam)

      newUrl = newUrlObj.toString()

      // console.debug('ChildPanelLayer - getDataFromService - newUrl', titleid, newUrl, newUrlObj.searchParams, currentTarget)
    } catch (error) {
      console.log('ChildPanelLayer - getDataFromService - URL construction failed', titleid, url, error)
    }
  }

  // console.debug('Download - downloadData - filterParam / newUrl', titleid, filterParam, newUrl)

  // Create new Url params object from new Url
  const newUrlParamsObj = PoziUtils.getUrlParamsObj(newUrl)

  const cacheId = generateCacheId(titleid, newUrl, currentTarget)

  const cacheTimeout = Model.get('config.cacheTimeoutSeconds', 60)
  const disableCache = Model.get('config.disableCache', false)

  if (!disableCache && cacheTimeout > 0) {
    const geojson = getPanelDataCache(cacheId)
    // console.log('getData - check for cache', cacheId)
    if (!isNil(geojson)) {
      // console.log('getData - returning cached data for', titleid, geojson)
      return callback(geojson)
    } else {
      // console.log('getData - no cached data found for', titleid)
    }
  }

  const key = get(layerConf, 'config.spatial.params.key', '')
  const tq = get(layerConf, 'config.spatial.params.tq', '')

  // Default to GeoJSON unless specified otherwise
  if (isNil(loadertypelower) || loadertypelower === '' || loadertypelower === 'geojson') {
    return getGeoJSONMarkup(newUrl, fallbackUrls, titleid, currentTarget, callback, getDataCallback, cacheId, layerConf)
  }

  switch (loadertypelower) {
    case 'ckanapi':
      PoziAPICKANAPI()
        .getDataAsGeoJSON(newUrl)
        .then(function (geojson) {
          getDataCallback(cacheId, layerConf, geojson, callback)
        }).catch((err) => errorRetrievingDataFn(err, callback))
      break
    case 'gml':
      PoziApiGml
        .getDataAsGeoJSON(newUrl)
        .then(function (geojson) {
          getDataCallback(cacheId, layerConf, geojson, callback)
        }).catch((err) => errorRetrievingDataFn(err, callback))
      break
    case 'gsheet':
      PoziAPIGSheet()
        .getDataAsGeoJSON(newUrl, key, tq)
        .then(function (geojson) {
          getDataCallback(cacheId, layerConf, geojson, callback)
        }).catch((err) => errorRetrievingDataFn(err, callback))
      break
    case 'hereaddress':
      GetAddressFromHereMaps()
        .getGeoJSON(newUrl)
        .then(function (geojson) {
          getDataCallback(cacheId, layerConf, geojson, callback)
        }).catch((err) => errorRetrievingDataFn(err, callback))
      break
    case 'jotform':
      PoziJotForm()
        .getGeoJSON(newUrl)
        .then(function (geojson) {
          getDataCallback(cacheId, layerConf, geojson, callback)
        }).catch((err) => errorRetrievingDataFn(err, callback))
      break
    case 'wmsgetfeatureinfogeoserver':
      WMSGetFeatureInfoGeoServerGetGeoJSON(newUrl, newUrlParamsObj, currentTarget, callback, getDataCallback, cacheId, layerConf)
      break
    default: {
      const message = `Loader type (${loadertypelower}) not yet supported for child layer '${titleid}'`

      // console.log('Download - getData - message, message)

      PoziNotifications().warning(message)
    }
  }
}

export const downloadFeatureCount = async (child, currentTarget) => {
  const { titleid, url } = child

  // let loader = loadertypelower

  const isEsri = url.toLowerCase().includes('/arcgis/')
  const isEsriFeatureServer = isEsri && url.toLowerCase().includes('/featureserver/')

  let newUrl = url

  // Build URL for fetching feature count
  try {
    newUrl = newUrl.replace(/outputformat=\w+/i, '') // By removing this, the default output format will be GML

    const newUrlObj = new URL(newUrl)
    if (isEsriFeatureServer) {
      newUrlObj.searchParams.set('ReturnCountOnly', 'true')
    } else {
      newUrlObj.searchParams.set('RESULTTYPE', 'hits')
    }

    // Add the spatial intersect filter param to the request (if needed)
    const filterParam = getFilterParam(child, currentTarget)
    if (filterParam) {
      newUrlObj.searchParams.set('FILTER', filterParam)
    }

    newUrl = newUrlObj.toString()

    // console.debug('downloadFeatureCount - newUrl', titleid, newUrl, newUrlObj.searchParams)
  } catch (error) {
    console.log('downloadFeatureCount - URL construction failed', titleid, error)
  }

  // Try to retrieve feature count from cache before (re-)requesting

  const cacheTimeout = Model.get('config.cacheTimeoutSeconds', 60)
  const disableCache = Model.get('config.disableCache', false)

  const cacheId = generateCacheId(titleid, newUrl)
  if (!disableCache && cacheTimeout > 0) {
    const featureCount = getPanelDataCache(cacheId)
    // console.log('downloadFeatureCount - check for cache', cacheId)
    if (!isNil(featureCount)) {
      console.log('downloadFeatureCount - returning cached data for', titleid, featureCount)
      return featureCount
    } else {
      // console.log('downloadFeatureCount - no cached data found for', titleid)
    }
  }

  let result
  try {
    result = await PoziAPIData().ajax({
      dataType: 'text',
      url: newUrl,
      error: function (_jqXHR, _textStatus, errorThrown) {
        console.log('downloadFeatureCount - LoaderFailed url', newUrl, _textStatus, errorThrown)
      }
    })
  } catch (error) {
    console.log('downloadFeatureCount - LoaderFailed url', newUrl, error)

    return null
  }

  let featureCount

  // Extract feature count from result.
  // Esri FeatureServer returns JSON, whereas WFS services will return XML
  if (isEsriFeatureServer) {
    try {
      const json = JSON.parse(result)
      featureCount = json?.properties?.count
    } catch (error) {
      console.log('ChildPanelLayer - getFeatureCountFromService - Esri JSON parse failed', titleid, error)
    }
  } else {
    featureCount = result.match(/(numberMatched|numberOfFeatures)="(\d+)"/)?.[2] ?? null
  }

  if (isNil(featureCount)) {
    // console.log('ChildPanelLayer - getFeatureCountFromService - No feature count found', titleid, result)
    return null
  }

  // Convert featureCount from string to a number
  featureCount = parseInt(featureCount, 10)

  // Store feature count in cache
  if (!disableCache) {
    setPanelDataCache(cacheId, featureCount)
  }

  return featureCount
}

export const getFilterParam = (child, currentTarget) => {
  const { titleid, loadertypelower, url, urlParamsObj } = child

  // console.debug('Download - getFilterParam', titleid, url, urlParamsObj, child, currentTarget)

  // If the request is WFS and does not have a FILTER param yet, we will create one for intersection
  // with the parent object
  const hasFilterParam = Object.keys(urlParamsObj).some(key => !!key.match(/filter/i))
  const needsFilter = loadertypelower === 'geojson' && urlParamsObj.SERVICE === 'WFS' && !hasFilterParam
  const { $gml } = currentTarget?.propertiesAll || {}

  // console.debug('Download - getFilterParam - needsFilter?', titleid, needsFilter, $gml, currentTarget)

  let filterParam

  // Add the spatial intersect filter param to the request. We assume here that the property name is called 'geom'
  if (needsFilter && $gml) {
    filterParam = `<Filter><Intersects><PropertyName>geom</PropertyName>${$gml}</Intersects></Filter>`
  }

  // console.debug('Download - getFilterParam - filterParam', titleid, filterParam)

  return filterParam
}

async function getGeoJSONMarkup (url, fallbackUrls, titleid, currentTarget, callback, getDataCallbackFn, cacheId, layerConf) {
  console.debug('Download - getGeoJSONMarkup', titleid, url, fallbackUrls, currentTarget, layerConf)

  let errorMsg = ''

  const requestPromises = []

  // Add main request without delay
  const firstRequestPromise = (async () => {
    // console.debug('First request promise', titleid, url)
    try {
      // console.debug('First request sending', titleid, url)
      const result = await getDataFromURL(url, layerConf.serverType)
      // console.debug('First request complete', titleid, result)
      return result
    } catch (error) {
      console.debug('Download - getGeoJSONMarkup - first request failed', titleid, url, error)
      errorMsg = error
      throw (error)
    }
  })()

  // Add our main request to our list of requests
  requestPromises.push(firstRequestPromise)

  // Try any fallback URLs if we were unsuccessful in getting child data
  if (fallbackUrls) {
    if (!(fallbackUrls instanceof Array)) {
      fallbackUrls = [fallbackUrls]
    }

    const fallbackRequestDelay = 5000

    for (const fallbackUrl of fallbackUrls) {
      // console.debug('getGeoJSONMarkup - creating fallback delayed request...', titleid, fallbackUrl)

      // Create special function per request that, after waiting for some time, attempts
      // to fetch the data unless another request has been successful before that.
      const delayedRequestPromise = (async () => {
        // console.debug(`getGeoJSONMarkup - Delayed request delayed for ${fallbackRequestDelay}ms.`, titleid, fallbackUrl)

        await PoziUtils.sleep(fallbackRequestDelay)

        // Only proceed if none of the other requests have resulted in success
        if (!childData) {
          // console.debug('getGeoJSONMarkup - Delayed request sending', titleid, fallbackUrl)
          try {
            const result = await getDataFromURL(fallbackUrl, layerConf.serverType)
            console.debug('Download - getGeoJSONMarkup - Delayed request complete', titleid, fallbackUrl, result)
            return result
          } catch (error) {
            // console.debug('getGeoJSONMarkup - Delayed request failed', titleid, fallbackUrl, error)
            errorMsg = error
            throw (error)
          }
        } else {
          console.debug('Download - getGeoJSONMarkup - Delayed request cancelled due to result received via other request', titleid, fallbackUrl)
        }
      })()

      // Add delayed fallback request to our list of requests
      requestPromises.push(delayedRequestPromise)
    }
  }

  let childData
  try {
    // We will use the data from the first successful request
    childData = await Promise.any(requestPromises)
    // console.debug('getGeoJSONMarkup - Request(s) complete. Data received', titleid, childData)
  } catch (error) {
    console.log('Download - getGeoJSONMarkup - No child data received: all requests failed!', titleid, error)
    errorMsg = error
  }

  if (!childData) {
    return errorRetrievingDataFn(errorMsg, callback)
  }

  // Clone the data as transformers below may otherwise change the original received data
  let geojson = cloneDeep(childData?.data ?? [])

  // Apply transformers to the data
  if (!isNil(layerConf)) {
    let coords = null
    const hasPointOnSurfaceTransformer = get(layerConf, 'config.transformer.pointOnSurface', false)
    if (hasPointOnSurfaceTransformer) {
      coords = currentTarget.featureCollectionCentroid?.geometry?.coordinates
    }

    geojson = ApplyTransformers(layerConf, geojson, coords)
  }

  // console.debug('Download - getGeoJSONMarkup - geojson', titleid, { ...geojson })

  // Try getting projection (CRS) info from either layer configuration or the actual request
  let projection = layerConf.config?.spatial?.projection || layerConf.config?.projection

  if (!projection) {
    try {
      const urlObj = new URL(url)
      const searchParamsObj = Object.fromEntries([...urlObj.searchParams]) // create object from search params

      if (Object.keys(searchParamsObj).includes('CRS')) {
        projection = searchParamsObj.CRS

        console.debug('Download - getGeoJSONMarkup - projection from URL', titleid, projection)
      }
    } catch (error) {
      console.log('Download - getGeoJSONMarkup - getting projection from URL failed', titleid, error)
    }
  } else {
    console.debug('Download - getGeoJSONMarkup - projection from layer config', titleid, projection)
  }

  if (projection && projection !== 'EPSG:4326') {
    console.debug('Download - getGeoJSONMarkup - reprojection needed', titleid, projection)

    // Reproject from the data projection to the new EPSG:7844 projection, which is similar
    // to EPGS:4326 but caters for the shift (GDA2020)
    // Does this actually ever get used??  Ideally we shouldn't ever project to GDA94 or GDA2020 specifically.
    const options = {
      dataProjection: projection,
      featureProjection: 'EPSG:7844'
    }

    const olFormatGeoJson = new OLFormatGeoJSON()

    try {
      const olFeatures = olFormatGeoJson.readFeatures(geojson, options)
      geojson = olFormatGeoJson.writeFeaturesObject(olFeatures)

      // console.debug('Download - getGeoJSONMarkup - new GeoJSON', titleid, { ...geojson })
    } catch (error) {
      console.log('Download - getGeoJSONMarkup - reprojection failed', titleid, error)
    }
  }

  getDataCallbackFn(cacheId, layerConf, geojson, callback)
}

async function WMSGetFeatureInfoGeoServerGetGeoJSON (url, urlParamsObj, currentTarget, callback, getDataCallbackFn, cacheId, layerConf) {
  if (isEmpty(urlParamsObj)) {
    // https://openlayers.org/en/latest/apidoc/module-ol_source_ImageWMS-ImageWMS.html
    console.log('Download - WMSGetFeatureInfoGeoServerGetGeoJSON - WMSGetFeatureInfoGeoServer requires at least a LAYERS param to work. See Openlayers ImageWMS source definition.')
    return
  }
  const options = {
    url,
    params: urlParamsObj,
    projection: 'EPSG:4326',
    serverType: 'geoserver',
    crossOrigin: 'Anonymous'
  }
  const wmsSource = new OLSourceImageWMS(options)

  const defaultParams = {
    INFO_FORMAT: 'application/json',
    FEATURE_COUNT: '10',
    BUFFER: '5',
    FORMAT: 'image/png8',
    FI_POINT_TOLERANCE: '10',
    FI_LINE_TOLERANCE: '10',
    FI_POLYGON_TOLERANCE: '5',
    WITH_GEOMETRY: 'TRUE'
  }

  const combinedParams = { ...defaultParams, ...urlParamsObj }

  // console.debug('Download - WMSGetFeatureInfoGeoServerGetGeoJSON - combinedParams', currentTarget.titleid, defaultParams, urlParamsObj)

  // console.log('WMSGetFeatureInfo params', combinedParams)
  // Tried to change this to get as EPSG:4857... didn't work. attempting to convert this once data retrieved instead.
  // console.log('getFeatureInfoUrl defaults to retrieve data as as EPSG:3857.')
  const longitude = get(currentTarget, 'propertiesAll.$longitude', null)
  const latitude = get(currentTarget, 'propertiesAll.$latitude', null)
  if (isNil(longitude) || isNil(latitude)) {
    console.log('Download - WMSGetFeatureInfoGeoServerGetGeoJSON - getFeatureInfoUrl unable to determine a coordinate from current target.. attempting to use click position!')
    return
  }
  const coords3857 = OLProjTransform([longitude, latitude], 'EPSG:4326', 'EPSG:3857')

  const mapView = window.appObject.map.getView()
  const getFeatureInfoURLString = wmsSource.getFeatureInfoUrl(coords3857, mapView.getResolution(), mapView.getProjection(), combinedParams)
  // console.debug('Download - WMSGetFeatureInfoGeoServerGetGeoJSON - getFeatureInfoUrl url', layerConf.titleid, getFeatureInfoURLString)

  try {
    const result = await getDataFromURL(getFeatureInfoURLString, layerConf.serverType)
    // console.debug('getFeatureInfoUrl data', result.data)
    getDataCallbackFn(cacheId, layerConf, result.data, callback)
  } catch (err) {
    console.log('Download - WMSGetFeatureInfoGeoServerGetGeoJSON - getFeatureInfoUrl: Failed to retrieve data from geoserver.')
    return errorRetrievingDataFn(err, callback)
  }
}

export function UsePost (url) {
  if (!url) { return false }

  // TODO: check if we still need to post to this service
  if (url.toLowerCase().includes('https://opendata.maps.vic.gov.au/geoserver/ows')) {
    return true
  }

  if (url.length > MAX_GET_URL_LENGTH_GET) {
    return true
  }

  return false
}

function getDataFromURL (url, serverType) {
  console.debug('Download - getDataFromURL', url, serverType)

  if (!url) {
    return Promise.reject(new Error('No URL provided'))
  }

  let method = 'GET'
  let data = null
  let contentType = null

  // Convert GET to POST if the URL is too long for Azure
  const usePost = UsePost(url)

  // console.debug('getDataFromURL - usePost?', usePost, url)

  if (usePost) {
    // console.debug('getDataFromURL - Using POST', url)

    try {
      const urlObj = new URL(url)
      const baseUrl = `${urlObj.origin}${urlObj.pathname}`
      const searchParamsObj = Object.fromEntries([...urlObj.searchParams]) // create object from search params

      method = 'POST'
      url = baseUrl
      data = searchParamsObj

      // QGIS Server needs special treatment
      if (serverType === 'qgis') {
        contentType = 'multipart/form-data'
      } else {
        contentType = 'application/x-www-form-urlencoded'
      }
    } catch (error) {
      console.error('getDataFromURL - usePost - error creating URL object from URL. Will use GET as fallback', url, error)
    }
  }

  return new Promise(function (resolve, reject) {
    PoziAPIData().ajax({
      contentType,
      data,
      dataType: 'json',
      method,
      url,
      error: function (err) {
        if (err.code === 'ECONNABORTED') {
          console.log('Timeout exceeded for ', url)
        } else {
          console.warn('Download - getDataFromURL -error', err.statusText, url, err)
        }
        return reject(err)
      },
      success: function (featureCollectionT) {
        return resolve({ data: featureCollectionT })
      }
    }).catch(function (error) {
      console.error('Download - getDataFromURL - unexpected error', url, error.responseText, error)
      reject(new Error('Unexpected error:' + (error.responseText ?? 'Unknown error')))
    })
  })
}
